/**
 * PersonaSwitcher — V23.13 Sprint 1
 *
 * Pill cliquable dans le topbar/sidebar qui affiche le package sectoriel
 * de l'org + le niveau UI de l'utilisateur. Clic = ouvre un modal pour
 * changer l'un et/ou l'autre.
 *
 * Sprint 1 : infrastructure seulement. Le manifest est récupéré mais pas
 * encore appliqué au rendu (Sprints 2-4).
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { broadcastPersonaChange } from "@/lib/persona";
import { Briefcase, GraduationCap, ChevronDown, X, Check, Loader2 } from "lucide-react";

export default function PersonaSwitcher({ compact = false, isAdmin = false }) {
  const [manifest, setManifest] = useState(null);
  const [options, setOptions] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/workspace/persona/me");
      setManifest(r.data);
    } catch { /* silent */ }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const r = await api.get("/workspace/persona/options");
      setOptions(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { refresh(); loadOptions(); }, [refresh, loadOptions]);

  if (!manifest) return null;

  const setPackage = async (key) => {
    setBusy(true);
    try {
      await api.put("/workspace/persona/org", { sector_package: key });
      await refresh();
      broadcastPersonaChange();
      notify.success(`Package « ${key} » activé pour l'organisation.`);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const setLevel = async (key) => {
    setBusy(true);
    try {
      await api.put("/workspace/persona/user", { ui_level: key });
      await refresh();
      broadcastPersonaChange();
      notify.success(`Niveau UI « ${key} » activé.`);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 ${
          compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"
        } rounded-full border border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#378ADD]/40 hover:text-white transition`}
        data-testid="persona-switcher-btn"
      >
        <Briefcase className="w-3 h-3 text-[#EF9F27]" />
        <span className="truncate max-w-[120px]">{manifest.sector_package_label}</span>
        <span className="text-zinc-600">·</span>
        <GraduationCap className="w-3 h-3 text-[#378ADD]" />
        <span className="truncate max-w-[90px]">{manifest.ui_level_label}</span>
        <ChevronDown className="w-3 h-3 text-zinc-500" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[90] bg-[#0A1A2F]/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="persona-switcher-modal"
        >
          <div
            className="w-full max-w-2xl bg-[#0d1626] border border-white/10 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-bold">
                  Modules adaptatifs · Sprint 1
                </div>
                <h2 className="text-lg text-white font-bold mt-0.5">Personnaliser votre environnement</h2>
              </div>
              <button onClick={() => setOpen(false)} disabled={busy} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Sector package (admin only) */}
              <div>
                <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-[#EF9F27] font-bold">
                  <Briefcase className="w-3 h-3" /> Package sectoriel
                  {!isAdmin && <span className="text-zinc-600 normal-case text-[9px] ml-1">(admin uniquement)</span>}
                </div>
                <div className="space-y-1.5">
                  {(options?.sector_packages || []).map((pkg) => {
                    const active = manifest.sector_package === pkg.key;
                    return (
                      <button
                        key={pkg.key}
                        onClick={() => isAdmin && !active && setPackage(pkg.key)}
                        disabled={!isAdmin || busy || active}
                        className={`w-full text-left p-2.5 rounded-lg border transition ${
                          active
                            ? "border-[#EF9F27]/50 bg-[#EF9F27]/10"
                            : "border-white/5 bg-white/[0.02] hover:border-[#EF9F27]/30 hover:bg-white/[0.04]"
                        } ${!isAdmin ? "opacity-60 cursor-not-allowed" : ""}`}
                        data-testid={`persona-pkg-${pkg.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-white font-semibold">{pkg.label}</span>
                          {active && <Check className="w-3 h-3 text-[#EF9F27]" />}
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{pkg.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* UI level (everyone) */}
              <div>
                <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-[#378ADD] font-bold">
                  <GraduationCap className="w-3 h-3" /> Niveau utilisateur
                </div>
                <div className="space-y-1.5">
                  {(options?.ui_levels || []).map((lvl) => {
                    const active = manifest.ui_level === lvl.key;
                    return (
                      <button
                        key={lvl.key}
                        onClick={() => !active && setLevel(lvl.key)}
                        disabled={busy || active}
                        className={`w-full text-left p-2.5 rounded-lg border transition ${
                          active
                            ? "border-[#378ADD]/50 bg-[#378ADD]/10"
                            : "border-white/5 bg-white/[0.02] hover:border-[#378ADD]/30 hover:bg-white/[0.04]"
                        }`}
                        data-testid={`persona-level-${lvl.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-white font-semibold">{lvl.label}</span>
                          {active && <Check className="w-3 h-3 text-[#378ADD]" />}
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5 leading-tight">{lvl.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-white/5 text-[10px] text-zinc-500 flex items-center justify-between">
              <span>
                <strong className="text-zinc-300">{manifest.visible_modules?.length ?? manifest.visible_modules_count ?? 0}</strong> module{(manifest.visible_modules?.length ?? 0) > 1 ? "s" : ""} visible{(manifest.visible_modules?.length ?? 0) > 1 ? "s" : ""} sur <strong className="text-zinc-300">{manifest.plan_modules_count ?? "?"}</strong> autorisé{manifest.plan_modules_count > 1 ? "s" : ""} par votre plan.
              </span>
              {busy && <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Application…</span>}
            </div>
            <div className="px-5 pb-4 text-[10px] text-zinc-600 italic leading-relaxed">
              Le plan de votre organisation est la seule source d&apos;autorisation. Le package sectoriel et le niveau ne font que filtrer la présentation — ils ne débloquent jamais de nouveaux modules.
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
